
import React, { useState } from "react";
import "../styles/staff-contract.css";

const API = (process.env.REACT_APP_API_BASE_URL || "/v1").replace(/\/$/, "");

const empty = {
  buyerName: "",
  buyerEmail: "",
  buyerPhone: "",
  adminName: "",
  adminEmail: "",
  productModel: "",
  featuresOptions: "",
  specifications: [],
  shortDetail: "",
  deliveryLeadTime: "",
  deliveryConditions: "",
  attachments: [],
  spareParts: [],
  productPrice: "",
  originalProductPrice: "",
  usualPrice: "",
  packagePrice: "",
  finalTotal: "",
  depositAmount: "0",
  discountAmount: "0",
  paymentType: "outright",
  financeMonths: 0,
  notes: "",
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const num = (value) => Number(value || 0);
const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const hasValue = (value) => value !== "" && value !== null && value !== undefined;

const itemPrice = (item, type) => {
  if (type === "original") {
    return num(hasValue(item.originalPrice) ? item.originalPrice : item.price);
  }
  return num(hasValue(item.packagePrice) ? item.packagePrice : item.price);
};

const calculate = (form) => {
  const allItems = [...form.attachments, ...form.spareParts];

  const originalMachine = num(
    hasValue(form.originalProductPrice)
      ? form.originalProductPrice
      : form.productPrice
  );

  const packageMachine = num(form.productPrice);

  const originalItems = allItems.reduce(
    (total, item) =>
      total + itemPrice(item, "original") * num(item.quantity),
    0
  );

  const packageItems = allItems.reduce(
    (total, item) =>
      total + itemPrice(item, "package") * num(item.quantity),
    0
  );

  const usual = round(originalMachine + originalItems);
  const subtotal = round(packageMachine + packageItems);
  const discount = round(num(form.discountAmount));
  const packageTotal = round(subtotal - discount);

  return {
    usual,
    subtotal,
    discount,
    packageTotal,
    savings: round(usual - packageTotal),
  };
};

export default function StaffContractPage() {
  const [form, setForm] = useState(empty);
  const [id, setId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [recent, setRecent] = useState([]);
  const [dirty, setDirty] = useState(false);

  const totals = calculate(form);
  const editable = ["draft", "send_failed"].includes(status);

  const set = (key, value) => {
    setDirty(true);
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const updateRow = (key, index, field, value) => {
    set(
      key,
      form[key].map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      )
    );
  };

  const removeRow = (key, index) => {
    set(key, form[key].filter((_, i) => i !== index));
  };

  const api = async (path, method = "GET", body) => {
    const token = localStorage.getItem("token");

    const response = await fetch(`${API}/staff-contracts${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token || ""}`,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      throw new Error(result.message || "Request failed");
    }

    return result;
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      await action();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const payload = () => {
    const convertItems = (items) =>
      items.map((item) => ({
        name: item.name.trim(),
        quantity: num(item.quantity),
        price: num(
          hasValue(item.packagePrice) ? item.packagePrice : item.price
        ),
        originalPrice: num(
          hasValue(item.originalPrice)
            ? item.originalPrice
            : item.price
        ),
        packagePrice: num(
          hasValue(item.packagePrice)
            ? item.packagePrice
            : item.price
        ),
      }));

    return {
      ...form,
      specifications: form.specifications.map((row) => ({
        label: row.label.trim(),
        value: row.value.trim(),
      })),
      attachments: convertItems(form.attachments),
      spareParts: convertItems(form.spareParts),
      productPrice: num(form.productPrice),
      originalProductPrice: num(
        hasValue(form.originalProductPrice)
          ? form.originalProductPrice
          : form.productPrice
      ),
      usualPrice: totals.usual,
      packagePrice: totals.packageTotal,
      finalTotal: totals.packageTotal,
      discountAmount: totals.discount,
      depositAmount: num(form.depositAmount),
      financeMonths: num(form.financeMonths),
    };
  };

  const save = () =>
    run(async () => {
      if (totals.packageTotal < 0) {
        throw new Error("Discount cannot exceed package subtotal.");
      }

      if (totals.usual < totals.packageTotal) {
        throw new Error(
          "Usually Price cannot be lower than Package Price."
        );
      }

      if (num(form.depositAmount) > totals.packageTotal) {
        throw new Error("Deposit cannot exceed Package Price.");
      }

      if (
        !form.specifications.length &&
        !form.featuresOptions.trim()
      ) {
        throw new Error("Add at least one machine specification.");
      }

      const result = await api(
        id ? `/${id}` : "/",
        id ? "PATCH" : "POST",
        payload()
      );

      setId(result.data._id);
      setStatus(result.data.status);
      setDirty(false);
      setNotice("Draft saved successfully. Review before sending.");
    });

  const send = () =>
    run(async () => {
      if (!id || dirty) {
        throw new Error("Please save your latest changes first.");
      }

      if (
        !window.confirm(
          "Send this test contract to the Buyer via DocuSign? Admin signs second."
        )
      ) {
        return;
      }

      const result = await api(`/${id}/send`, "POST");

      setStatus(result.data.status);
      setNotice("Contract sent to Buyer. Admin will sign second.");
      setPreview(false);
    });

  const refresh = () =>
    run(async () => {
      const result = await api(`/${id}/refresh-status`, "POST");
      setStatus(result.data.status);
      setNotice(`DocuSign status: ${result.remoteStatus}`);
    });

  const loadRecent = () =>
    run(async () => {
      const result = await api("/");
      setRecent(result.data);
    });

  const load = (draftId) =>
    run(async () => {
      const result = await api(`/${draftId}`);

      setForm({
        ...empty,
        ...result.data,
        specifications: result.data.specifications || [],
        attachments: result.data.attachments || [],
        spareParts: result.data.spareParts || [],
      });

      setId(result.data._id);
      setStatus(result.data.status);
      setDirty(false);
      setPreview(false);
    });

  const reset = () => {
    setForm({ ...empty });
    setId(null);
    setStatus("draft");
    setDirty(false);
    setPreview(false);
    setError("");
    setNotice("");
  };

  const field = (key, label, type = "text", required = false) => (
    <label className="sc-field">
      {label}{required ? " *" : ""}
      <input
        className="sc-input"
        type={type}
        required={required}
        min={type === "number" ? 0 : undefined}
        step={type === "number" ? "0.01" : undefined}
        value={form[key] ?? ""}
        disabled={!editable}
        onChange={(event) => set(key, event.target.value)}
      />
    </label>
  );

  const textArea = (key, label, rows = 4, required = false) => (
    <label className="sc-field">
      {label}{required ? " *" : ""}
      <textarea
        className="sc-input"
        rows={rows}
        required={required}
        disabled={!editable}
        value={form[key] ?? ""}
        onChange={(event) => set(key, event.target.value)}
      />
    </label>
  );

  const specificationEditor = () => (
    <section className="sc-spec-section">
      <div className="sc-row">
        <h3>Machine Specifications</h3>
        <button
          className="sc-secondary"
          type="button"
          disabled={!editable}
          onClick={() =>
            set("specifications", [
              ...form.specifications,
              { label: "", value: "" },
            ])
          }
        >
          + Add Specification
        </button>
      </div>

      <p className="sc-help">
        Each row will appear as a bullet point in DocuSign.
      </p>

      {form.specifications.map((spec, index) => (
        <div className="sc-spec-row" key={index}>
          <span className="sc-spec-dot">●</span>

          <input
            className="sc-input"
            placeholder="Feature name (e.g. Mast)"
            value={spec.label}
            disabled={!editable}
            onChange={(event) =>
              updateRow(
                "specifications",
                index,
                "label",
                event.target.value
              )
            }
          />

          <input
            className="sc-input"
            placeholder="Specification value"
            value={spec.value}
            disabled={!editable}
            onChange={(event) =>
              updateRow(
                "specifications",
                index,
                "value",
                event.target.value
              )
            }
          />

          <button
            className="sc-secondary"
            type="button"
            disabled={!editable}
            onClick={() => removeRow("specifications", index)}
          >
            Remove
          </button>
        </div>
      ))}

      {!form.specifications.length && (
        <p className="sc-empty">
          No structured specifications added. Add rows above or use
          the legacy Features field below.
        </p>
      )}

      <details className="sc-legacy">
        <summary>Legacy Features & Options text</summary>
        {textArea("featuresOptions", "Features & Options", 5)}
      </details>
    </section>
  );

  const itemEditor = (key, title) => (
    <section className="sc-panel">
      <div className="sc-row">
        <h2>{title}</h2>
        <button
          type="button"
          className="sc-secondary"
          disabled={!editable}
          onClick={() =>
            set(key, [
              ...form[key],
              {
                name: "",
                quantity: 1,
                price: "",
                originalPrice: "",
                packagePrice: "",
              },
            ])
          }
        >
          + Add
        </button>
      </div>

      {form[key].length > 0 && (
        <div className="sc-item-head">
          <span>Item</span>
          <span>Qty</span>
          <span>Usually / Unit</span>
          <span>Package / Unit</span>
          <span></span>
        </div>
      )}

      {form[key].map((item, index) => (
        <div className="sc-item sc-item-pricing" key={index}>
          <input
            className="sc-input"
            placeholder="Item name"
            aria-label="Item name"
            value={item.name}
            disabled={!editable}
            onChange={(event) =>
              updateRow(key, index, "name", event.target.value)
            }
          />

          <input
            className="sc-input"
            type="number"
            min="1"
            step="1"
            aria-label="Quantity"
            value={item.quantity}
            disabled={!editable}
            onChange={(event) =>
              updateRow(key, index, "quantity", event.target.value)
            }
          />

          <input
            className="sc-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Usually AUD"
            aria-label="Usually unit price"
            value={item.originalPrice ?? ""}
            disabled={!editable}
            onChange={(event) =>
              updateRow(
                key,
                index,
                "originalPrice",
                event.target.value
              )
            }
          />

          <input
            className="sc-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Package AUD"
            aria-label="Package unit price"
            value={item.packagePrice ?? ""}
            disabled={!editable}
            onChange={(event) =>
              updateRow(
                key,
                index,
                "packagePrice",
                event.target.value
              )
            }
          />

          <button
            className="sc-secondary"
            type="button"
            disabled={!editable}
            onClick={() => removeRow(key, index)}
          >
            Remove
          </button>
        </div>
      ))}

      {!form[key].length && (
        <p className="sc-empty">No items added yet.</p>
      )}
    </section>
  );

  const options = [
    ...form.attachments,
    ...form.spareParts,
  ];

  return (
    <main className="sc-page">
      <div className="sc-wrap">
        <header className="sc-header">
          <div>
            <small>SYM MACHINERY • STAFF TOOLS</small>
            <h1>Manual Contract Generator</h1>
            <p>
              Prepare an offline machine contract. Buyer signs first,
              followed by SYM Admin.
            </p>
          </div>
          <b>DOCUSIGN DEMO</b>
        </header>

        <nav className="sc-row sc-toolbar">
          <button
            type="button"
            className="sc-secondary"
            onClick={reset}
          >
            New Contract
          </button>

          <button
            type="button"
            className="sc-secondary"
            onClick={loadRecent}
            disabled={busy}
          >
            Recent Contracts
          </button>

          {id && <span>Status: {status.toUpperCase()}</span>}
        </nav>

        {recent.length > 0 && (
          <section className="sc-panel">
            <h2>Recent Contracts</h2>
            {recent.map((record) => (
              <button
                type="button"
                className="sc-record"
                key={record._id}
                onClick={() => load(record._id)}
              >
                {record.buyerName} • {record.productModel} • {record.status}
              </button>
            ))}
          </section>
        )}

        {error && <div className="sc-error">{error}</div>}
        {notice && <div className="sc-success">{notice}</div>}

        {preview ? (
          <section className="sc-panel">
            <h2>Contract Preview</h2>
            <p className="sc-help">
              This is a data preview. Final document appearance
              depends on the DocuSign template.
            </p>

            <p><strong>Buyer:</strong> {form.buyerName} ({form.buyerEmail})</p>
            <p><strong>Admin:</strong> {form.adminName} ({form.adminEmail})</p>

            <div className="sc-contract-preview">
              <h3>Features & Options</h3>

              {form.specifications.length ? (
                <div className="sc-preview-specs">
                  {form.specifications.map((spec, index) => (
                    <div className="sc-preview-spec" key={index}>
                      <strong>● {spec.label}</strong>
                      <span>{spec.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <pre>{form.featuresOptions}</pre>
              )}

              <h3>Delivery Lead Time Information</h3>
              <p><strong>{form.deliveryLeadTime || "To be confirmed"}</strong></p>
              <pre>{form.deliveryConditions}</pre>

              <h3>Attachments & Spare Parts</h3>

              <div className="sc-preview-options">
                {options.map((item, index) => (
                  <div className="sc-preview-option" key={index}>
                    <span>
                      ({index + 1}) {item.name} ×{item.quantity}
                    </span>
                    <strong>
                      {money(
                        itemPrice(item, "package") *
                        num(item.quantity)
                      )}
                    </strong>
                  </div>
                ))}
              </div>

              <div className="sc-usual-banner">
                TOTAL USUALLY {money(totals.usual)} + GST
              </div>

              <div className="sc-package-banner">
                PACKAGE PRICE — Total {money(totals.packageTotal)} ex GST
              </div>
            </div>

            <h3>Proposal Details</h3>
            <pre>{form.shortDetail}</pre>

            <div className="sc-row sc-actions">
              <button
                type="button"
                className="sc-secondary"
                onClick={() => setPreview(false)}
              >
                Back to form
              </button>

              <button
                type="button"
                className="sc-primary"
                disabled={busy || dirty || !id || !editable}
                onClick={send}
              >
                Send to Buyer via DocuSign
              </button>
            </div>
          </section>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <section className="sc-panel">
              <h2>1. Customer Details</h2>
              <div className="sc-grid">
                {field("buyerName", "Buyer name", "text", true)}
                {field("buyerEmail", "Buyer email", "email", true)}
                {field("buyerPhone", "Buyer phone", "tel")}
              </div>
            </section>

            <section className="sc-panel">
              <h2>2. SYM Admin Signer</h2>
              <div className="sc-grid">
                {field("adminName", "Admin full name", "text", true)}
                {field("adminEmail", "Admin email", "email", true)}
              </div>
            </section>

            <section className="sc-panel">
              <h2>3. Machine Information</h2>
              {field("productModel", "Machine / Model", "text", true)}
              {specificationEditor()}
              <div className="sc-delivery">
                <h3>Delivery Lead Time Information</h3>
                {textArea("deliveryLeadTime", "Delivery lead time", 3)}
                {textArea("deliveryConditions", "Delivery conditions", 5)}
              </div>
              {textArea(
                "shortDetail",
                "Proposal Short Details",
                5,
                true
              )}
            </section>

            {itemEditor("attachments", "4. Attachments & Options")}
            {itemEditor("spareParts", "5. Spare Parts")}

            <section className="sc-panel">
              <h2>6. Pricing</h2>

              <div className="sc-grid">
                {field(
                  "originalProductPrice",
                  "Machine Usually Price AUD",
                  "number"
                )}
                {field(
                  "productPrice",
                  "Machine Package Price AUD",
                  "number",
                  true
                )}
                {field(
                  "discountAmount",
                  "Additional Discount AUD",
                  "number"
                )}
                {field(
                  "depositAmount",
                  "Deposit AUD",
                  "number",
                  true
                )}
              </div>

              <div className="sc-price-summary">
                <div>
                  <span>Total Usually</span>
                  <strong>{money(totals.usual)}</strong>
                </div>
                <div>
                  <span>Package Subtotal</span>
                  <strong>{money(totals.subtotal)}</strong>
                </div>
                <div>
                  <span>Additional Discount</span>
                  <strong>− {money(totals.discount)}</strong>
                </div>
                <div className="sc-final-line">
                  <span>Final Package Price (ex GST)</span>
                  <strong>{money(totals.packageTotal)}</strong>
                </div>
              </div>

              <div className="sc-grid">
                <label className="sc-field">
                  Payment Type
                  <select
                    className="sc-input"
                    value={form.paymentType}
                    disabled={!editable}
                    onChange={(event) =>
                      set("paymentType", event.target.value)
                    }
                  >
                    <option value="outright">Outright Purchase</option>
                    <option value="finance">Finance</option>
                  </select>
                </label>

                {form.paymentType === "finance" &&
                  field(
                    "financeMonths",
                    "Finance Months",
                    "number",
                    true
                  )}
              </div>

              {textArea("notes", "Internal Notes", 3)}
            </section>

            <div className="sc-row sc-actions">
              <button
                type="submit"
                className="sc-primary"
                disabled={busy || !editable}
              >
                {busy
                  ? "Saving..."
                  : id
                    ? "Update Draft"
                    : "Save Draft"}
              </button>

              <button
                type="button"
                className="sc-secondary"
                onClick={() => setPreview(true)}
              >
                Preview
              </button>

              {id && status === "sent" && (
                <button
                  type="button"
                  className="sc-secondary"
                  onClick={refresh}
                >
                  Refresh Status
                </button>
              )}

              {id && status === "completed" && (
                <button
                  type="button"
                  className="sc-secondary"
                  onClick={() =>
                    window.open(
                      `${API}/staff-contracts/${id}/pdf`,
                      "_blank"
                    )
                  }
                >
                  View Signed PDF
                </button>
              )}
            </div>
          </form>
        )}

        <footer>
          DocuSign Demo Environment Only. Existing website checkout
          and production DocuSign integration remain unchanged.
        </footer>
      </div>
    </main>
  );
}
